import type {
  TBenefits,
  TCandidateCompensationPackage,
} from '@model/Candidate/Candidate.type';
import type {
  TGlobalCompensationBenefit,
  TGlobalCompensationBonus,
  TGlobalCompensationProps,
} from '@components/Organisms/GlobalCompensation/GlobalCompensation.types';
import { SORTED_BENEFIT_TYPES } from '@components/Organisms/GlobalCompensation/_constants/bonuses-and-benefits.constants';
import { BONUS_AND_BENEFITS_STATUSES } from '@model/Candidate/constants/types.constants';
import type { Nullable } from '@utils/types/Nullable';

interface TUseValuesReturn {
  annualSalary: Nullable<number>;
  bonuses: TGlobalCompensationBonus[];
  benefits: TGlobalCompensationBenefit[];
  contractLevel: Nullable<string>;
  isEstimated: boolean;
  total: number;
}

export const getCompensationPackageValues = ({
  candidate,
  isDesired,
}: TGlobalCompensationProps): TUseValuesReturn => {
  const compensationPackage = candidate.compensationPackage;

  if (!compensationPackage) {
    return {
      annualSalary: null,
      bonuses: [],
      benefits: [],
      contractLevel: null,
      isEstimated: false,
      total: 0,
    };
  }

  const { currentBenefits, desiredBenefits } = getBenefitsByStatus(
    compensationPackage.benefits,
  );

  const { currentBonuses, desiredBonuses } = getBonusesByStatus({
    fixedValueBonuses: compensationPackage.fixedValueBonuses,
    grossAnnualSalaryPercentageBonuses:
      compensationPackage.grossAnnualSalaryPercentageBonuses,
  });

  const {
    isEstimated: isCurrentCompensationPackageEstimated,
    total: currentCompensationPackageTotal,
  } = getCompensationPackageTotal({
    annualSalary: compensationPackage.currentAnnualSalary,
    bonuses: currentBonuses,
    benefits: currentBenefits,
  });

  const {
    isEstimated: isDesiredCompensationPackageEstimated,
    total: desiredCompensationPackageTotal,
  } = getCompensationPackageTotal({
    annualSalary: compensationPackage.desiredAnnualSalary,
    bonuses: desiredBonuses,
    benefits: desiredBenefits,
  });

  const annualSalary = isDesired
    ? compensationPackage.desiredAnnualSalary
    : compensationPackage.currentAnnualSalary;

  const bonuses = isDesired ? desiredBonuses : currentBonuses;
  const benefits = isDesired ? desiredBenefits : currentBenefits;

  const contractLevel = isDesired
    ? compensationPackage.desiredContractLevel
    : compensationPackage.currentContractLevel;

  const total = isDesired
    ? desiredCompensationPackageTotal
    : currentCompensationPackageTotal;

  const isEstimated = isDesired
    ? isDesiredCompensationPackageEstimated
    : isCurrentCompensationPackageEstimated;

  return {
    annualSalary,
    benefits,
    bonuses,
    contractLevel,
    isEstimated,
    total,
  };
};

interface TGetBenefitsByStatusReturn {
  currentBenefits: TGlobalCompensationBenefit[];
  desiredBenefits: TGlobalCompensationBenefit[];
}

export const getBenefitsByStatus = (
  benefits: TBenefits[] = [],
): TGetBenefitsByStatusReturn => {
  let currentBenefits: TGlobalCompensationBenefit[] = [];
  let desiredBenefits: TGlobalCompensationBenefit[] = [];

  SORTED_BENEFIT_TYPES.forEach(benefitType => {
    const matchingBenefit = benefits.find(
      benefit => benefit.type === benefitType,
    );

    if (!matchingBenefit) {
      return;
    }

    if (matchingBenefit.isCurrent) {
      currentBenefits = [
        ...currentBenefits,
        {
          details: matchingBenefit.currentDetails,
          type: matchingBenefit.type,
          value: matchingBenefit.currentValue,
        },
      ];
    }

    if (matchingBenefit.isDesired) {
      desiredBenefits = [
        ...desiredBenefits,
        {
          details: matchingBenefit.desiredDetails,
          type: matchingBenefit.type,
          value: matchingBenefit.desiredValue,
        },
      ];
    }
  });

  return { currentBenefits, desiredBenefits };
};

type TGetBonusesByStatusPayload = Pick<
  TCandidateCompensationPackage,
  'fixedValueBonuses' | 'grossAnnualSalaryPercentageBonuses'
>;

interface TGetBonusesByStatusReturn {
  currentBonuses: TGlobalCompensationBonus[];
  desiredBonuses: TGlobalCompensationBonus[];
}

export const getBonusesByStatus = ({
  fixedValueBonuses = [],
  grossAnnualSalaryPercentageBonuses = [],
}: TGetBonusesByStatusPayload): TGetBonusesByStatusReturn => {
  let currentBonuses: TGlobalCompensationBonus[] = [];
  let desiredBonuses: TGlobalCompensationBonus[] = [];

  fixedValueBonuses.forEach((bonus): void => {
    const outputBonus = {
      details: bonus.details,
      value: bonus.value,
    };

    if (bonus.status === BONUS_AND_BENEFITS_STATUSES.CURRENT) {
      currentBonuses = [...currentBonuses, outputBonus];
      return;
    }

    desiredBonuses = [...desiredBonuses, outputBonus];
  });

  grossAnnualSalaryPercentageBonuses.forEach(bonus => {
    const outputBonus = {
      details: bonus.details,
      isPercentage: true,
      value: bonus.grossAnnualSalaryPercentage,
    };

    if (bonus.status === BONUS_AND_BENEFITS_STATUSES.CURRENT) {
      currentBonuses = [...currentBonuses, outputBonus];
      return;
    }

    desiredBonuses = [...desiredBonuses, outputBonus];
  });

  return { currentBonuses, desiredBonuses };
};

interface TGetCompensationPackageTotalPayload {
  annualSalary: Nullable<number>;
  bonuses: TGlobalCompensationBonus[];
  benefits: TGlobalCompensationBenefit[];
}

interface TGetCompensationPackageTotalReturn {
  isEstimated: boolean;
  total: number;
}

export const getCompensationPackageTotal = ({
  annualSalary,
  benefits,
  bonuses,
}: TGetCompensationPackageTotalPayload): TGetCompensationPackageTotalReturn => {
  const benefitsSubTotal = getBenefitsSubTotal(benefits);
  const bonusesSubTotal = getBonusesSubTotal({ annualSalary, bonuses });
  const annualSalarySubTotal = Number(annualSalary ?? 0);

  return {
    isEstimated: benefitsSubTotal + bonusesSubTotal > 0,
    total: annualSalarySubTotal + benefitsSubTotal + bonusesSubTotal,
  };
};

const getBenefitsSubTotal = (benefits: TGlobalCompensationBenefit[]): number =>
  benefits.reduce((subTotal, benefit) => {
    if (!benefit.value) {
      return subTotal;
    }

    return subTotal + Number(benefit.value);
  }, 0);

interface TGetBonusesSubTotalPayload {
  annualSalary: Nullable<number>;
  bonuses: TGlobalCompensationBonus[];
}

export const getBonusesSubTotal = ({
  annualSalary,
  bonuses,
}: TGetBonusesSubTotalPayload): number =>
  bonuses.reduce((subTotal, bonus) => {
    if (!bonus.value) {
      return subTotal;
    }

    if (!bonus.isPercentage) {
      return subTotal + Number(bonus.value);
    }

    if (!annualSalary) {
      return subTotal;
    }

    const bonusFixedValue = annualSalary * (Number(bonus.value) / 100);

    return subTotal + bonusFixedValue;
  }, 0);
